import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Sentiment } from '../../api/transcriptions';
import { useSelector } from '../../hook-utils';
import { ButtonResponse, DialogButtons, DialogInputs, showTeamsShowInputDialog } from '../../legacy';
import { CallBookmark, TagType, TranscriptionParagraph } from '../../model';
import { actions } from '../../store';
import { selectDateFormating, selectMyself } from '../../store/reducers/config';
import { transcriptionSearchFilters } from '../../store/reducers/localSettings';
import { selectMedia } from '../../store/reducers/media';
import { selectTranscription, selectTranscriptionScore } from '../../store/reducers/transcriptions';
import { formatRelativeDateTime } from '../AgentsTable';
import { useDebouncedCallback } from '../Autocomplete';
import Checkbox from '../Checkbox';
import { ReactComponent as RightIcon } from '../icons/chevron-down.svg';
import { ReactComponent as LeftIcon } from '../icons/chevron-up.svg';
import { ReactComponent as CloseIcon } from '../icons/cross.svg';
import { ReactComponent as DeleteIcon } from '../icons/delete.svg';
import { ReactComponent as EditIcon } from '../icons/edit.svg';
import { ReactComponent as BookMarkIcon } from '../icons/tag.svg';
import { ReactComponent as HistoryIcon } from '../icons/toolbar-calls.svg';
import { ReactComponent as SearchIcon } from '../icons/toolbar-search.svg';
import { ReactComponent as NegativeIcon } from '../icons/transcription/rating-1.svg';
import { ReactComponent as NeutralIcon } from '../icons/transcription/rating-3.svg';
import { ReactComponent as PositiveIcon } from '../icons/transcription/rating-5.svg';
import { ReactComponent as UndoIcon } from '../icons/transcription/Undo.svg';
import Input from '../Input';
import { Menu, MenuDivider, MenuItem, MenuTarget, targetFromEvent } from '../Menu';
import MoreButton from '../MoreButton';
import { EditableText } from './TasksPage';
import './Transcription.scss';

interface Props {
  mediaId: string
}

interface MenuState {
  target: MenuTarget
  paragraph?: TranscriptionParagraph
  tag?: CallBookmark
  mediaId: string
}

export default function TranscriptionDetailsPage({ mediaId }: Props) {
  return <div className='TranscriptionPageDetails'>
    <TranscriptionView mediaId={mediaId} />
  </div>
}

export function TranscriptionViewPanel({ mediaId }: { mediaId: string }) {
  return <div className='TranscriptionViewPanel'>
    <TranscriptionView mediaId={mediaId} />
  </div>
}

export function TranscriptionView({ mediaId }: { mediaId: string }) {
  const refScroll = useRef<HTMLDivElement>(null)
  const ref = useRef<HTMLDivElement>(null)
  const myId = useSelector(selectMyself)
  const data = useSelector(s => selectTranscription(s, mediaId))
  const score = useSelector(s => selectTranscriptionScore(s, mediaId))
  const dispatch = useDispatch()
  const [menu, setMenu] = useState<MenuState>()
  const [isEditing, setEditing] = useState('')
  const [selectedParagraph, setSelectedParagraph] = useState('')
  const [editingBookmark, setEditingBookmark] = useState('')
  const format = useSelector(selectDateFormating)
  const filters = useSelector(transcriptionSearchFilters)
  const media = useSelector(s => selectMedia(s, mediaId))

  const [searchTerm, setSearchTerm] = useState('')
  const [search, setSearch] = useState('')
  const searchRefs = useRef<HTMLDivElement[]>([]);
  const [searchRefsIndex, setSearchRefsIndex] = useState<number>(-1)
  const [searchRefsLength, setSearchRefsLength] = useState<number>(0)

  const scrollMessages = useCallback((smooth: boolean) => {
    if (refScroll.current !== null && ref.current !== null && isEditing === '') {
      const { scrollTop, scrollHeight, clientHeight } = refScroll.current
      //if at bottom keep at the bottom 
      if (scrollHeight - (scrollTop + clientHeight) < 100) {
        ref.current.scrollIntoView({ behavior: smooth ? 'smooth' : undefined })
      }
    }
  }, [isEditing])

  useEffect(() => {
    //unlikely to get here with out answering the call as the push updated would already have loaded this
    // otherwise historical load

    if (!data) {
      dispatch(actions.fetchCallTranscriptionRequest(mediaId))
    }
  }, [dispatch, data, mediaId])

  // on first load scroll to the end without animation
  useEffect(() => {
    if (media)
      scrollMessages(false)
  }, [media, scrollMessages])

  // on new message scroll with animation
  useEffect(() => {
    if (media)
      scrollMessages(true)
  }, [data, media, scrollMessages]);

  const resetSearch = useCallback(() => {
    setSearch('')
    runSearch('')
  }, [])

  const runSearch = (value: string) => {
    setSelectedParagraph('')
    setSearchRefsIndex(-1)
    searchRefs.current = []
    setSearchRefsLength(0)

    setSearchTerm(value)
  }

  const startMatchTo = useDebouncedCallback(async (value: string) => {
    runSearch(value)
  }, 500)

  //scroll on find
  const scrollOnFindMessages = useCallback((smooth: boolean) => {
    if (refScroll.current !== null && ref.current !== null && searchRefs.current.length > 0 && searchRefsIndex > -1) {
      const el = searchRefs.current[searchRefsIndex]
      if (el) {
        el.scrollIntoView({ behavior: smooth ? 'smooth' : undefined })
        setSelectedParagraph(el.id)
      }
    }
  }, [searchRefsIndex])

  useEffect(() => {
    if (searchRefsIndex > -1)
      scrollOnFindMessages(true)
  }, [scrollOnFindMessages, searchRefsIndex])

  const moveUp = useCallback(() => {
    if (searchRefsIndex === 0) {
      return
    }
    setSearchRefsIndex(searchRefsIndex - 1)
  }, [searchRefsIndex])

  const moveDown = useCallback(() => {
    if (searchRefsIndex === searchRefs.current.length - 1) {
      return
    }
    setSearchRefsIndex(searchRefsIndex + 1)
  }, [searchRefsIndex])

  useEffect(() => {
    return () => {
      setSearch('')
      runSearch('')
    }
  }, [mediaId])

  const results = useMemo(() => {
    if (searchRefsLength > 0) {
      return <div className='results'><div>{searchRefsIndex + 1}/{searchRefsLength} </div> <LeftIcon onClick={() => moveUp()} className={'icon'} /> <RightIcon onClick={() => moveDown()} className={'icon'} /> <CloseIcon onClick={() => resetSearch()} className={'icon'} /></div>
    }
    return <div></div>
  }, [moveDown, moveUp, resetSearch, searchRefsIndex, searchRefsLength])

  if (!format) return null
  if (!myId) return null

  const onMatchRefs = (el: HTMLDivElement) => {
    if (el && searchRefs.current.findIndex(x => x.id === el.id) === -1) {
      searchRefs.current.push(el);
    }

    if (searchRefs.current.length === 1) {
      setSearchRefsIndex(0)
    }

    setSearchRefsLength(searchRefs.current.length)
  }

  return <>
    <h2>Transcription</h2>
    <div className='filters'>
      <div className='buttons'>
        <button className='icon' title="Create Bookmark" key="cc-tag-normal" onClick={() => {
          const p = data ? selectedParagraph !== '' ? data.find(x => x.paragraphId === selectedParagraph) : data[data.length - 1] : undefined
          showTeamsShowInputDialog('Bookmark', 'Please enter a bookmark', '', DialogButtons.OkCancel, DialogInputs.TextFull, (button: ButtonResponse, value: string[]) => {
            if (button === 'ok') {
              dispatch(actions.requestCallTranscriptionTag(mediaId, value[0], TagType.Normal, p ? p.createdAtUtc : undefined))
            }
          })
        }
        } ><BookMarkIcon /></button>

        <button className='icon red' title="Create Priority Bookmark" key="cc-tag-priority" onClick={() => {
          const p = data ? selectedParagraph !== '' ? data.find(x => x.paragraphId === selectedParagraph) : data[data.length - 1] : undefined
          showTeamsShowInputDialog('Priority Bookmark', 'Please enter a priority bookmark', '', DialogButtons.OkCancel, DialogInputs.TextFull, (button: ButtonResponse, value: string[]) => {
            if (button === 'ok') {
              dispatch(actions.requestCallTranscriptionTag(mediaId, value[0], TagType.Priority, p ? p.createdAtUtc : undefined))
            }
          })
        }
        } ><BookMarkIcon /></button>
      </div>
      <div className='types'>
        <Checkbox title={!filters.bookmarks ? 'Show Bookmark Details' : 'Hide Bookmark Details'} label={'Bookmarks'} checked={filters.bookmarks} onChange={(e) => {
          dispatch(actions.updateTranscriptionFilter({ ...filters, bookmarks: e.currentTarget.checked }))
        }
        } />
        <Checkbox title={!filters.agent ? 'Show agent transcriptions' : 'Hide agent transcriptions'} label='Agent' checked={filters.agent} onChange={(e) => {
          dispatch(actions.updateTranscriptionFilter({ ...filters, agent: e.currentTarget.checked }))
        }
        } />
        <Checkbox title={!filters.caller ? 'Show caller transcriptions' : 'Hide caller transcriptions'} label='Caller' checked={filters.caller} onChange={(e) => {
          dispatch(actions.updateTranscriptionFilter({ ...filters, caller: e.currentTarget.checked }))
        }
        } />
      </div>
      <div className='searchBox'>
        <Input placeholder='text...' className='search'
          value={search} onChange={e => {
            setSearch(e.currentTarget.value)
            startMatchTo(e.currentTarget.value)
          }} autocomplete={false} />
        <div className='searchResults'>
          <SearchIcon />
          {results}
        </div>
      </div>
    </div>
    <div className='TranscriptionView' ref={refScroll} >
      {!data && <div>Loading...</div>}
      {data && data.filter(f => ((filters.agent && f.extensionId) || (filters.caller && (!f.extensionId)))).map((d, i) => {
        return <ParagraphDraw key={'pd' + d.paragraphId}
          paragraph={d}
          selectedParagraph={selectedParagraph === d.paragraphId}
          editingParagraph={isEditing === d.paragraphId}
          editingBookmarkId={editingBookmark}
          mediaId={mediaId}
          showBookMarks={filters.bookmarks}
          setSelectedParagraph={setSelectedParagraph}
          setEditingParagraph={setEditing}
          setMenu={setMenu}
          setEditingBookmark={setEditingBookmark}
          search={searchTerm}
          onMatch={onMatchRefs} />
      })}
      <div ref={ref} className='last'></div>
    </div>
    <div className={classNames('Sentment', sentimentClass(score.score))} title={'Positive : ' + score.positive + '\nNeutral : ' + score.neutral + '\nNegative : ' + score.negative}></div>

    {menu && menu.paragraph && <ParagraphContextMenu state={menu} onClose={() => setMenu(undefined)} onEdit={(id) => {
      setEditing(id)
      setSelectedParagraph('')
      setEditingBookmark('')
    }} />
    }

    {menu && menu.tag && <TagContextMenu state={menu} onClose={() => setMenu(undefined)} onEdit={(id) => {
      setEditing('')
      setSelectedParagraph('')
      setEditingBookmark(id)
    }} />
    }

  </>
}

function tagsTheSame(prev?: CallBookmark[], next?: CallBookmark[]) {
  const state = (prev === undefined && next === undefined) || (prev !== undefined && next !== undefined && prev.length === next.length && next.every(t => prev !== undefined && prev.some(z => z.bookmarkId === t.bookmarkId && z.modifiedAt === t.modifiedAt)))
  return state
}

const ParagraphDraw = React.memo(Paragraph, (prev, next) => {
  return prev.selectedParagraph === next.selectedParagraph
    && prev.editingParagraph === next.editingParagraph
    && prev.editingBookmarkId === next.editingBookmarkId
    && prev.mediaId === next.mediaId
    && prev.showBookMarks === next.showBookMarks
    && prev.search === next.search

    && prev.paragraph.paragraphId === next.paragraph.paragraphId
    && prev.paragraph.sentiment === next.paragraph.sentiment
    && prev.paragraph.modifiedAtUtc === next.paragraph.modifiedAtUtc

    && tagsTheSame(prev.paragraph.tags, next.paragraph.tags)


}) as typeof Paragraph

function Paragraph({ paragraph, selectedParagraph, editingParagraph, editingBookmarkId, mediaId, showBookMarks, setSelectedParagraph, setEditingParagraph, setMenu, setEditingBookmark, onMatch, search }: { paragraph: TranscriptionParagraph, selectedParagraph: boolean, editingParagraph: boolean, editingBookmarkId: string, mediaId: string, showBookMarks: boolean, setSelectedParagraph(value: string): void, setEditingParagraph(value: string): void, setMenu(value: MenuState): void, setEditingBookmark(value: string): void, onMatch(ref: HTMLDivElement): void, search?: string }) {
  const format = useSelector(selectDateFormating)
  const dispatch = useDispatch()
  const [isEditing, setEditing] = useState('')
  const [isSelected, setIsSelected] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (search && paragraph.text.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1) {
      if (ref.current !== null) {
        onMatch(ref.current)
      }
    }
  }, [onMatch, paragraph.text, search])

  if (!format) return <></>

  return <div id={paragraph.paragraphId} ref={ref} className={classNames('transcript', paragraph.modifiedBy && 'edited', paragraph.extensionId && 'agent', sentimentClass(paragraph.sentiment))} key={paragraph.paragraphId} title={'Positive : ' + paragraph.positive + '\nNeutral : ' + paragraph.neutral + '\nNegative : ' + paragraph.negative} onClick={() => {
    setSelectedParagraph(paragraph.paragraphId)
  }} >
    <div className={classNames('paragraph', (selectedParagraph || editingParagraph) && 'selected')}>
      <div>
        <b>{paragraph.displayName ? paragraph.displayName : paragraph.telephoneNumber ? paragraph.telephoneNumber : 'Unknown'}</b> - {dayjs(paragraph.createdAtUtc).format(format.longTimeFormat)} {paragraph.modifiedBy && <span title={'Modified by extension ' + paragraph.modifiedBy + ' ' + formatRelativeDateTime(paragraph.modifiedAtUtc, format.shortDateFormat, format.shortTimeFormat)}><HistoryIcon /></span>}  {!showBookMarks && paragraph.tags !== undefined && showTags(paragraph.tags)}
      </div>
      <div className='onmore'>
        <MoreButton className='menu-button' onClick={e => {
          e.stopPropagation()

          setMenu({ target: targetFromEvent(e), paragraph: paragraph, mediaId: mediaId })
          setSelectedParagraph(paragraph.paragraphId)

          setEditing('')
          setEditingBookmark('')
          setIsSelected('')
        }} />
      </div>
      <div>
        <EditableText type='textarea' value={paragraph.text} hightlight={search} onSave={text => {
          dispatch(actions.updateCallTranscriptionText(mediaId, paragraph.paragraphId, text))
          setEditingParagraph('')
        }} onBlur={() => {
          //NEEDED TO FIRE TO REDRAW
          setEditingParagraph('')
        }}
          editing={editingParagraph} editable={paragraph.canEdit} icon={false} />
      </div>
    </div>
    {showBookMarks && paragraph.tags !== undefined && <div className='tags' onClick={(e) => {
      e.stopPropagation()
    }}>
      {paragraph.tags.map(t => {
        return <div className={classNames('tag', (isSelected === t.bookmarkId || isEditing === t.bookmarkId || editingBookmarkId === t.bookmarkId) && 'selected')} key={t.bookmarkId}>
          <div className={classNames(t.bookmarkTypeId === TagType.Priority && 'red', t.bookmarkTypeId === TagType.Normal && 'normal')}><BookMarkIcon /></div>
          <div><EditableText type='textarea' value={t.title ? t.title : ''} onSave={text => {
            dispatch(actions.requestCallTranscriptionTag(mediaId, text, t.bookmarkTypeId, t.timeStamp, t.bookmarkId))
            setEditing('')
            setEditingBookmark('')
            setIsSelected('')
          }}
            onBlur={() => {
              //NEEDED TO FIRE TO REDRAW
              setEditing('')
              setEditingBookmark('')
              setIsSelected('')
            }}

            onEdit={() => {
              if (editingBookmarkId !== t.bookmarkId) {
                setEditing(t.bookmarkId)
                setEditingBookmark(t.bookmarkId)
                setIsSelected(t.bookmarkId)
              }
            }} editing={isEditing === t.bookmarkId || editingBookmarkId === t.bookmarkId} editable={paragraph.canEdit} icon={false} />{}</div>

          <div className='onmore'>
            <MoreButton className='menu-button' onClick={e => {
              e.stopPropagation()

              setSelectedParagraph('')
              setMenu({ target: targetFromEvent(e), tag: t, mediaId: mediaId })

              setEditing('')
              setEditingBookmark('')

              setIsSelected(t.bookmarkId)
            }} />
          </div>
        </div>
      })}
    </div>}
  </div>
}

function sentimentClass(sentiment: Sentiment) {
  return sentiment === Sentiment.neutral ? 'neutral' : sentiment === Sentiment.negative ? 'negative' : sentiment === Sentiment.positive ? 'positive' : sentiment === Sentiment.mixed ? 'mixed' : ''
}

function showTags(tags?: CallBookmark[]) {
  if (!tags) return null
  return tags.map(t => {
    if (t.bookmarkTypeId === TagType.Priority) {
      return <div className='tag red' title={t.title}><BookMarkIcon /> </div>
    }
    return <div className='tag normal' title={t.title}><BookMarkIcon /> </div>
  });
}

function ParagraphContextMenu({ state: { paragraph, target, mediaId }, onClose, onEdit }: { state: MenuState, onClose(): void, onEdit(id: string): void }) {
  const dispatch = useDispatch()
  if (!paragraph) return null

  return <Menu open={true} onClose={onClose} target={target} placement='bottom-end'>
    <MenuItem disabled={!paragraph.canEdit} onClick={() => onEdit(paragraph.paragraphId)} >
      <EditIcon />
      Edit
  </MenuItem>
    <MenuItem disabled={paragraph.original === undefined} onClick={() => dispatch(actions.requestTranscriptionParagraphRevert(mediaId, paragraph.paragraphId))} >
      <UndoIcon className={'padded'} />
      Revert
  </MenuItem>
    <MenuDivider />
    <MenuItem onClick={() => dispatch(actions.requestSetSentiment(mediaId, paragraph.paragraphId, Sentiment.positive))} >
      <PositiveIcon />
      Positive
  </MenuItem>
    <MenuItem onClick={() => dispatch(actions.requestSetSentiment(mediaId, paragraph.paragraphId, Sentiment.neutral))} >
      <NeutralIcon />
      Neutral
  </MenuItem>
    <MenuItem onClick={() => dispatch(actions.requestSetSentiment(mediaId, paragraph.paragraphId, Sentiment.negative))} >
      <NegativeIcon />
      Negative
  </MenuItem>
    <MenuDivider />
    <MenuItem onClick={() => {
      showTeamsShowInputDialog('Bookmark', 'Please enter a bookmark', '', DialogButtons.OkCancel, DialogInputs.TextFull, (button: ButtonResponse, value: string[]) => {
        if (button === 'ok') {
          dispatch(actions.requestCallTranscriptionTag(mediaId, value[0], TagType.Normal, paragraph.createdAtUtc))
        }
      })
    }} >
      <BookMarkIcon />
      Normal
  </MenuItem>
    <MenuItem onClick={() => {
      showTeamsShowInputDialog('Priority Bookmark', 'Please enter a priority bookmark', '', DialogButtons.OkCancel, DialogInputs.TextFull, (button: ButtonResponse, value: string[]) => {
        if (button === 'ok') {
          dispatch(actions.requestCallTranscriptionTag(mediaId, value[0], TagType.Priority, paragraph.createdAtUtc))
        }
      })
    }} itemClass='red' >
      <BookMarkIcon />
      Priority
  </MenuItem>
    {/*<MenuItem disabled={!paragraph.updated} onClick={() => dispatch(actions.showClientNotification('To be completed'))} >
      <HistoryIcon />
      History
  </MenuItem>*/}
  </Menu>
}

function TagContextMenu({ state: { tag, target, mediaId }, onClose, onEdit }: { state: MenuState, onClose(): void, onEdit(id: string): void }) {
  const dispatch = useDispatch()
  if (!tag) return null
  return <Menu open={true} onClose={onClose} target={target} placement='bottom-end'>
    <MenuItem onClick={() => onEdit(tag.bookmarkId)} >
      <EditIcon />
      Edit
  </MenuItem>
    <MenuItem onClick={() => { dispatch(actions.requestDeleteCallTranscriptionTag(mediaId, tag.bookmarkId)) }} >
      <DeleteIcon />
      Delete
  </MenuItem>
  </Menu>
}
